import React from 'react'
import {
  styled,
  TextField,
  type TextFieldProps,
  type Theme,
} from '@mui/material'
import {
  PickersTextField,
  type PickersTextFieldProps,
} from '@mui/x-date-pickers/PickersTextField'

// Mono uppercase label sitting statically above the field — shared by the
// plain text field and the date/time picker field.
const monoLabelStyles = (theme: Theme) => ({
  '& .MuiInputLabel-root': {
    position: 'static' as const,
    transform: 'none',
    marginBottom: theme.spacing(0.5),
    fontFamily: theme.app.monoFontFamily,
    fontSize: 10,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: theme.palette.neutral.gray,
    '&.Mui-focused': { color: theme.palette.neutral.gray },
    '&.Mui-error': { color: theme.palette.neutral.gray },
  },
})

export const CheckoutTextField = styled((props: TextFieldProps) => (
  <TextField
    variant="outlined"
    fullWidth
    {...props}
    slotProps={{
      ...props.slotProps,
      inputLabel: { ...props.slotProps?.inputLabel, shrink: true },
    }}
  />
))(({ theme }) => ({
  ...monoLabelStyles(theme),
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiOutlinedInput-notchedOutline': {
    top: 0,
    borderColor: theme.palette.neutral.lightGray,
  },
  '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.grey[200] },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-input': {
    padding: theme.spacing(1.25, 1.5),
  },
  '&& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': { padding: 0 },
  '&& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
    padding: theme.spacing(1.25, 1.5),
  },
}))

// Pickers v9 only support the accessible (PickersSectionList) DOM structure, so
// the date/time field must extend PickersTextField — passing a plain Material
// TextField to the `textField` slot leaks section props onto an <input> and
// throws. Mirror CheckoutTextField's styling against the Pickers* classes.
export const CheckoutPickerField = styled((props: PickersTextFieldProps) => (
  // shrink the label so MUI restores the empty-field section placeholder
  // (MM/DD/YYYY) — v9 hides it while the label is unshrunk. Matches
  // CheckoutTextField, whose static label is always shrunk.
  <PickersTextField
    variant="outlined"
    fullWidth
    {...props}
    slotProps={{
      ...props.slotProps,
      inputLabel: { ...props.slotProps?.inputLabel, shrink: true },
    }}
  />
))(({ theme }) => ({
  ...monoLabelStyles(theme),
  '& .MuiPickersOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiPickersOutlinedInput-notchedOutline': {
    top: 0,
    borderColor: theme.palette.neutral.lightGray,
  },
  '& .MuiPickersOutlinedInput-root': {
    backgroundColor: theme.palette.grey[200],
  },
  '& .MuiPickersInputBase-sectionsContainer': {
    padding: theme.spacing(1.25, 1.5),
  },
}))
